import { useCallback, useEffect, useRef, useState } from 'react';

import { CONSENT_TYPE_SHARE_SELL, getConsentPreferences, updateConsentPreferences } from 'apis/consent';
import { trackOperation, trackOperationFailureIfAuthed } from 'events/ops/opsEvent';
import { fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import { logErrorAndLogToServer } from 'middleware/logger';
import type { ConsentPreference } from 'apis/consent';
import type { ConsentStatus, CookieSignalType, TollhouseState } from 'types/trustarc';

interface UseTrustArcConsentOptions {
  isAuthenticated?: boolean;
  accountConfig?: { url: string };
}

interface UseTrustArcConsentReturn {
  consentStatus: ConsentStatus | null;
  updateConsent: (status: ConsentStatus) => void;
}

export const POLL_INTERVAL_MS = 300;
export const MAX_POLL_ATTEMPTS = 40; // ~12s

// Level 1 = required/functional, Level 2 = advertising/targeting.
const CONSENT_LEVELS_OPT_IN = [1, 2];
const CONSENT_LEVELS_OPT_OUT = [1];

/**
 * Read the two TrustArc cookies the consent flow depends on. Each field is the cookie's value,
 * or `null` when the cookie is absent (raw notice_gdpr_prefs values look like '0,1:' or '0:').
 */
function parseTrustArcCookies(): { noticeBehavior: string | null; gdprPrefs: string | null } {
  const behaviorMatch = document.cookie.match(/notice_behavior=([^;]*)/);
  const prefsMatch = document.cookie.match(/notice_gdpr_prefs=([^;]*)/);
  return {
    noticeBehavior: behaviorMatch?.[1] ?? null,
    gdprPrefs: prefsMatch?.[1] ?? null
  };
}

/**
 * Read consent state from the notice_gdpr_prefs cookie.
 */
export function readConsentFromCookie(): ConsentStatus {
  const { gdprPrefs } = parseTrustArcCookies();
  return gdprPrefs?.includes('1') ? 'ACCEPT_ALL' : 'REJECT_ALL';
}

/**
 * True only when the user has an explicit TrustArc choice recorded. Implied consent returns
 * false: notice_gdpr_prefs can't distinguish "opted out" from "no choice yet", so the initial
 * radio state must come from Tollhouse, not the cookie.
 */
export function hasExplicitConsentCookie(): boolean {
  const { gdprPrefs } = parseTrustArcCookies();
  return !!gdprPrefs;
}

/**
 * Classify the TrustArc cookie state for reconciliation.
 *
 * Signal hierarchy (strongest to weakest):
 * 1. Explicit user action (banner accept/reject)
 * 2. Prior Tollhouse record
 * 3. Implied consent via session cookie (US only)
 */
export function classifyCookieSignal(): CookieSignalType {
  const { noticeBehavior, gdprPrefs } = parseTrustArcCookies();

  if (noticeBehavior?.includes('eu')) {
    return 'EU';
  }

  if (gdprPrefs !== null) {
    return gdprPrefs.includes('1') ? 'EXPLICIT_ACCEPT' : 'EXPLICIT_REJECT';
  }

  if (noticeBehavior?.includes('implied')) {
    return 'IMPLIED_US';
  }

  return 'NO_SIGNAL';
}

/**
 * Expected-write correlation. When reconciliation or Privacy Save programmatically calls
 * setConsentViaApi, it fires prefclose as a side effect. The global event bridge must NOT
 * treat that prefclose as a user-initiated banner click and double-write to Tollhouse.
 *
 * Record before the CMA call; consumed by the bridge's prefclose handler.
 */
interface ExpectedWrite {
  choice: ConsentStatus;
  expiresAt: number;
}

const EXPECTED_WRITE_TTL_MS = 2000;
let expectedWrite: ExpectedWrite | undefined;

export function setConsentViaApi(status: ConsentStatus): void {
  const levels = status === 'ACCEPT_ALL' ? CONSENT_LEVELS_OPT_IN : CONSENT_LEVELS_OPT_OUT;

  expectedWrite = {
    choice: status,
    expiresAt: Date.now() + EXPECTED_WRITE_TTL_MS
  };

  try {
    window.truste?.cma?.callApi('setConsentLevels', window.location.hostname, levels);
  } catch {
    // CMA call failed — clear the expected write so the bridge doesn't suppress the next event
    expectedWrite = undefined;
  }
}

/**
 * Consume a pending expected write if the observed status matches.
 * Returns true if the event was self-initiated (bridge should skip Tollhouse write).
 */
export function consumeExpectedConsentWrite(observed: ConsentStatus): boolean {
  const expected = expectedWrite;
  expectedWrite = undefined;

  if (!expected) return false;
  if (expected.expiresAt < Date.now()) return false;
  return expected.choice === observed;
}

// --- Session checkpoint ---
// Once reconciliation succeeds, mark this login session as reconciled so subsequent
// page loads don't re-fire the Tollhouse GET. Stores a fingerprint of the x-main
// cookie — if x-main changes (logout + re-login) or disappears (logout), the
// checkpoint becomes invalid and reconciliation re-fires.

const CHECKPOINT_KEY = 'zappos:consent-reconciled:v1';

function getXMainFingerprint(): string | null {
  const match = document.cookie.match(/x-main=([^;]*)/);
  return match?.[1]?.slice(-12) || null;
}

export function hasConsentCheckpoint(): boolean {
  try {
    const stored = localStorage.getItem(CHECKPOINT_KEY);
    if (!stored) return false;
    const fingerprint = getXMainFingerprint();
    return !!fingerprint && stored === fingerprint;
  } catch {
    return false;
  }
}

export function markConsentCheckpoint(): void {
  const fingerprint = getXMainFingerprint();
  if (!fingerprint) return;
  try {
    localStorage.setItem(CHECKPOINT_KEY, fingerprint);
  } catch {
    // localStorage unavailable
  }
}

export function clearConsentCheckpoint(): void {
  try {
    localStorage.removeItem(CHECKPOINT_KEY);
  } catch {
    // localStorage unavailable
  }
}

export function toTollhouseAction(status: ConsentStatus): string {
  return status === 'ACCEPT_ALL' ? 'ACCEPT' : 'REJECT';
}

export function fromTollhousePreferences(preferences: ConsentPreference[]): TollhouseState {
  if (!preferences || preferences.length === 0) {
    return 'NO_RECORD';
  }
  const shareSell = preferences.find(p => p.consentType === CONSENT_TYPE_SHARE_SELL);
  if (!shareSell || !shareSell.userAction) {
    return 'NO_RECORD';
  }
  return shareSell.userAction === 'REJECT' ? 'OPT_OUT' : 'OPT_IN';
}

/**
 * Safely read a numeric `status` off an unknown thrown value. The GET path throws a FetchError
 * (which carries `.status`); network failures throw a plain Error with none.
 */
function getErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error) {
    const { status } = error;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
}

/**
 * Log a consent API failure in the op-name shape the alarm runbook's Splunk queries key off
 * (mirrors FavoritesDataLoad / SubscriptionDataLoad in hearts.js / subscriptions.ts, and Kratos'
 * useConsentReconciliation `logger.error`). Logs ALL failures, including 403 — the 403 suppression
 * lives in trackOperationFailureIfAuthed (the failure *metric*, which the alarm reads), not the log.
 */
function logConsentFailure(
  operation: 'ConsentPreferencesLoad' | 'ConsentPreferencesUpdate',
  reason: string,
  status: number | undefined,
  error?: unknown
): void {
  logErrorAndLogToServer(
    JSON.stringify({
      tag: 'Consent',
      operation,
      reason,
      status,
      error: error instanceof Error ? error.toString() : error
    })
  );
}

/**
 * Write consent to Tollhouse. Returns true on success, false on failure.
 * Errors are swallowed because the TrustArc cookie still reflects the user's choice
 * and reconciliation will retry next page load.
 */
export async function writeTollhouseConsent(accountConfig: { url: string }, status: ConsentStatus): Promise<boolean> {
  try {
    const response = await updateConsentPreferences(accountConfig, {
      consentType: CONSENT_TYPE_SHARE_SELL,
      userAction: toTollhouseAction(status)
    });
    if (response.status >= 400) {
      trackOperationFailureIfAuthed('ConsentPreferencesUpdate', response.status);
      logConsentFailure(
        'ConsentPreferencesUpdate',
        'writeTollhouseConsent API call rejected — unable to persist consent choice to Tollhouse',
        response.status
      );
      return false;
    }
    trackOperation('ConsentPreferencesUpdate', 'success');
    return true;
  } catch (error) {
    trackOperationFailureIfAuthed('ConsentPreferencesUpdate');
    logConsentFailure(
      'ConsentPreferencesUpdate',
      'writeTollhouseConsent API call rejected — unable to persist consent choice to Tollhouse',
      undefined,
      error
    );
    return false;
  }
}

/**
 * Fetch the Tollhouse consent state. Returns null on failure so callers fall back gracefully.
 * Emits ConsentPreferencesLoad ops events; 403s stay out of the failure metric via
 * trackOperationFailureIfAuthed (the recognized-but-unauthenticated GETs are expected, not failures).
 */
export async function fetchTollhouseConsent(accountConfig: { url: string }): Promise<TollhouseState | null> {
  try {
    const response = await getConsentPreferences(accountConfig);
    const preferences: ConsentPreference[] = await fetchErrorMiddleware(response);
    trackOperation('ConsentPreferencesLoad', 'success');
    return fromTollhousePreferences(preferences);
  } catch (error) {
    const status = getErrorStatus(error);
    trackOperationFailureIfAuthed('ConsentPreferencesLoad', status);
    logConsentFailure('ConsentPreferencesLoad', 'fetchTollhouseConsent API call rejected — unable to load Tollhouse consent record', status, error);
    return null;
  }
}

/**
 * Reconciliation decision engine. See CookieSignalType docs for the signal hierarchy.
 */
export function reconcile(
  signal: CookieSignalType,
  tollhouse: TollhouseState
): {
  action: 'NONE' | 'SYNC_COOKIE_REJECT' | 'WRITE_OPT_IN' | 'WRITE_OPT_OUT';
} {
  if (signal === 'EU' || signal === 'NO_SIGNAL') {
    return { action: 'NONE' };
  }

  if (signal === 'IMPLIED_US') {
    if (tollhouse === 'OPT_OUT') {
      return { action: 'SYNC_COOKIE_REJECT' };
    }
    return { action: 'NONE' };
  }

  if (signal === 'EXPLICIT_ACCEPT') {
    if (tollhouse === 'OPT_OUT' || tollhouse === 'NO_RECORD') {
      return { action: 'WRITE_OPT_IN' };
    }
    return { action: 'NONE' };
  }

  if (signal === 'EXPLICIT_REJECT') {
    if (tollhouse === 'OPT_OUT') {
      return { action: 'NONE' };
    }
    return { action: 'WRITE_OPT_OUT' };
  }

  return { action: 'NONE' };
}

/**
 * Bidirectional consent sync between the TrustArc footer modal and the
 * My Account Privacy Preferences radios, with Tollhouse persistence.
 *
 * Direction 1 (modal -> radios): Patches `truste.eu.prefclose` to detect
 * cookie changes, syncs React state, and writes to Tollhouse.
 *
 * Direction 2 (radios -> TrustArc + Tollhouse): Calls the CMA API to update
 * the cookie and writes to Tollhouse for server-side record.
 *
 * Two effects (A and B below) resolve `consentStatus` from different authorities; each
 * documents its own role and how they coordinate.
 */
const useTrustArcConsent = (options: UseTrustArcConsentOptions = {}): UseTrustArcConsentReturn => {
  const { isAuthenticated = false, accountConfig } = options;

  const [consentStatus, setConsentStatus] = useState<ConsentStatus | null>(null);
  const originalPrefCloseRef = useRef<(() => void) | null>(null);

  // Marks our own CMA writes so the prefclose handler can tell them from a footer-modal edit.
  // CMA can fire prefclose synchronously and clobber the state we just set.
  const isSelfInitiatedWriteRef = useRef(false);

  // Deduplicates Tollhouse writes: only POST when the cookie value actually changes.
  const lastKnownStatusRef = useRef<ConsentStatus | null>(null);

  // Holds a write requested before CMA finished loading, so the poll can flush it once ready.
  const pendingConsentWriteRef = useRef<ConsentStatus | null>(null);

  // Refs so async callbacks (prefclose, reconciliation .then) always see current values.
  const isAuthenticatedRef = useRef(isAuthenticated);
  const accountConfigRef = useRef(accountConfig);
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
    accountConfigRef.current = accountConfig;
  }, [isAuthenticated, accountConfig]);

  // Reconcile once per mount. A ref (not a dependency) so a new accountConfig reference
  // from the memoized selector can't re-fire it.
  const hasReconciledRef = useRef(false);

  // Write the TrustArc cookie via CMA, flagging it as ours. The 500ms window covers the burst
  // of async prefclose calls CMA fires after setConsentLevels (see isSelfInitiatedWriteRef).
  const writeCookieViaCma = useCallback((status: ConsentStatus) => {
    isSelfInitiatedWriteRef.current = true;
    setConsentViaApi(status);
    setTimeout(() => {
      isSelfInitiatedWriteRef.current = false;
    }, 500);
  }, []);

  // EFFECT A — Reconciliation. Authority: Tollhouse (the server-side record).
  // Runs once on the first authenticated render: fetches the Tollhouse state and aligns the
  // cookie + display with it. Writes to Tollhouse only when the cookie signal and record disagree.
  useEffect(() => {
    if (!isAuthenticated || !accountConfigRef.current || hasReconciledRef.current) {
      return;
    }

    hasReconciledRef.current = true;

    const runReconciliation = async () => {
      // Re-narrow: the ref type stays nullable even though the effect guard already bailed on null.
      const config = accountConfigRef.current;
      if (!config) {
        return;
      }

      const signal = classifyCookieSignal();

      // EU: match the radios to the current Tollhouse record for display, but never write to
      // Tollhouse or TrustArc.
      if (signal === 'EU') {
        const euTollhouseState = await fetchTollhouseConsent(config);
        const euStatus = euTollhouseState === 'OPT_OUT' ? 'REJECT_ALL' : 'ACCEPT_ALL';
        setConsentStatus(euStatus);
        lastKnownStatusRef.current = euStatus;
        return;
      }

      const tollhouseState = await fetchTollhouseConsent(config);
      if (tollhouseState === null) {
        // Tollhouse unavailable. An explicit cookie choice was already applied by the poll.
        // Without one, fall back to the opted-in default so the radios resolve to a definite
        // state instead of hanging unresolved.
        if (!hasExplicitConsentCookie()) {
          setConsentStatus(prev => prev ?? 'ACCEPT_ALL');
        }
        return;
      }

      const { action } = reconcile(signal, tollhouseState);

      if (action === 'WRITE_OPT_OUT') {
        writeTollhouseConsent(config, 'REJECT_ALL');
      } else if (action === 'WRITE_OPT_IN') {
        writeTollhouseConsent(config, 'ACCEPT_ALL');
      } else if (action === 'SYNC_COOKIE_REJECT') {
        // Prior opt-out wins over implied consent: push the reject down to the TrustArc cookie.
        // Route through writeCookieViaCma so isSelfInitiatedWriteRef is set before CMA can fire a
        // synchronous prefclose — otherwise that handler would see the change as a footer-modal
        // edit and POST a redundant opt-out. Display is owned by the trailing block below.
        writeCookieViaCma('REJECT_ALL');
      }

      // When there's no explicit cookie (TrustArc blocked or implied-only), use Tollhouse
      // as the display authority since the user hasn't made an explicit choice this session.
      if (signal === 'NO_SIGNAL' || signal === 'IMPLIED_US') {
        const tollhouseAsStatus = tollhouseState === 'OPT_OUT' ? 'REJECT_ALL' : 'ACCEPT_ALL';
        setConsentStatus(tollhouseAsStatus);
        lastKnownStatusRef.current = tollhouseAsStatus;
      }
    };

    runReconciliation();
  }, [isAuthenticated]);

  // EFFECT B — Availability poll. Authority: the TrustArc cookie + CMA API (the client-side signal).
  // Polls until the TrustArc script loads (or times out): reads the initial cookie state, flushes
  // any consent write queued before CMA was ready, and patches prefclose to catch footer-modal changes.
  useEffect(() => {
    let hasReadCookie = false;
    let hasSeenCma = false;
    let hasPatched = false;
    let attempts = 0;
    let interval: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const initialize = () => {
      const eu = window.truste?.eu;
      const cma = window.truste?.cma;

      // Resolve the radios from an explicit cookie choice without waiting on CMA. Covers the
      // returning user whose TrustArc script is blocked (ad blocker): EFFECT A only sets
      // consentStatus for implied/no-signal, so without this the radios stay blank forever.
      // Excluded on purpose:
      //  - EU: handled by EFFECT A (its cookie would be stale — EU never loads the TrustArc script).
      //  - Implied / no-signal: readConsentFromCookie can't tell "opted out" from "no choice yet",
      //    so it would flash REJECT_ALL — defer those to Tollhouse reconciliation.
      if (!hasReadCookie && hasExplicitConsentCookie() && classifyCookieSignal() !== 'EU') {
        const initialStatus = readConsentFromCookie();
        setConsentStatus(initialStatus);
        lastKnownStatusRef.current = initialStatus;
        hasReadCookie = true;
      }

      if (!hasSeenCma && cma) {
        hasSeenCma = true;
      }

      // Flush a consent write that was requested before CMA loaded.
      if (cma && pendingConsentWriteRef.current) {
        const pendingStatus = pendingConsentWriteRef.current;
        pendingConsentWriteRef.current = null;
        writeCookieViaCma(pendingStatus);
      }

      if (!hasPatched && eu?.prefclose) {
        originalPrefCloseRef.current = eu.prefclose;
        eu.prefclose = () => {
          originalPrefCloseRef.current?.();

          if (isSelfInitiatedWriteRef.current) {
            isSelfInitiatedWriteRef.current = false;
            return;
          }

          const newStatus = readConsentFromCookie();
          setConsentStatus(newStatus);
          lastKnownStatusRef.current = newStatus;
        };
        hasPatched = true;
      }

      attempts += 1;
      if ((hasSeenCma && hasPatched) || attempts >= MAX_POLL_ATTEMPTS) {
        stopPolling();
      }
    };

    initialize();
    if (!(hasSeenCma && hasPatched)) {
      interval = setInterval(initialize, POLL_INTERVAL_MS);
    }

    return () => {
      stopPolling();
      if (hasPatched && window.truste?.eu && originalPrefCloseRef.current) {
        window.truste.eu.prefclose = originalPrefCloseRef.current;
      }
    };
  }, [writeCookieViaCma]);

  const updateConsent = useCallback(
    (status: ConsentStatus) => {
      setConsentStatus(status);

      // EU: preserve the selection in local state only. The consent bridge takes no action for EU
      if (classifyCookieSignal() === 'EU') {
        return;
      }

      // Write the TrustArc cookie whenever the CMA API is available.
      // If CMA hasn't loaded yet (e.g. slow network), stash the write so the availability
      // poll can flush it once CMA appears. If CMA never loads (ad blocker), the cookie
      // can't be written by design — Tollhouse remains the authoritative record.
      if (window.truste?.cma) {
        writeCookieViaCma(status);
      } else {
        pendingConsentWriteRef.current = status;
      }

      if (isAuthenticatedRef.current && accountConfigRef.current) {
        lastKnownStatusRef.current = status;
        writeTollhouseConsent(accountConfigRef.current, status);
      }
    },
    [writeCookieViaCma]
  );

  return { consentStatus, updateConsent };
};

export default useTrustArcConsent;
